import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@/ui/primitives';
import { r, s } from '../../theme/tokens';
import { LastUpdated } from './LastUpdated';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement } from 'react';

type ServerDataTableFrameProps = {
  containerStyles: Array<Exclude<SxProps<Theme>, readonly unknown[] | boolean | null | undefined>>;
  dataError: string | null;
  dataState: string;
  dataTable: ReactElement | null;
  hasRows: boolean;
  isFetching: boolean;
  lastUpdatedAt: number | null;
  placeholder: ReactElement | null;
  resolvedHeight?: number;
  showLastUpdated: boolean;
  showPlaceholderGrid: boolean;
  showSkeletonGrid: boolean;
  skeleton: ReactElement | null;
  skeletonHeightAnchor: ReactElement | null;
};

type SxObject = Exclude<SxProps<Theme>, readonly unknown[]>;

export const ServerDataTableFrame = ({
  containerStyles,
  dataError,
  dataState,
  dataTable,
  hasRows,
  isFetching,
  lastUpdatedAt,
  placeholder,
  resolvedHeight,
  showLastUpdated,
  showPlaceholderGrid,
  showSkeletonGrid,
  skeleton,
  skeletonHeightAnchor,
}: ServerDataTableFrameProps): ReactElement => {
  const rowMeasureRef = useRef<HTMLDivElement | null>(null);
  const previousDataStateRef = useRef(dataState);
  const [measuredRowHeights, setMeasuredRowHeights] = useState<readonly number[]>([]);
  const [readyTableMeasured, setReadyTableMeasured] = useState(false);
  const measureRows = useCallback(() => {
    const rowMeasureElement = rowMeasureRef.current;
    if (!rowMeasureElement) return;
    const rows = Array.from(rowMeasureElement.querySelectorAll<HTMLElement>('.MuiDataGrid-row'));
    const nextHeights = rows
      .map((row) => row.getBoundingClientRect().height)
      .filter((height) => height > 0);
    if (nextHeights.length === 0) return;
    setMeasuredRowHeights((previousHeights) => {
      if (
        previousHeights.length === nextHeights.length &&
        previousHeights.every((height, index) => height === nextHeights[index])
      ) {
        return previousHeights;
      }
      return nextHeights;
    });
  }, []);

  useLayoutEffect(() => {
    if (dataState === 'loading') {
      setReadyTableMeasured(false);
      setMeasuredRowHeights((previousHeights) =>
        previousHeights.length === 0 ? previousHeights : [],
      );
    }
    measureRows();
    previousDataStateRef.current = dataState;
  }, [dataState, measureRows]);

  const measuredSkeletonRowSx = useMemo((): SxObject | null => {
    if (measuredRowHeights.length === 0) return null;
    const sxBySelector: Record<string, SxObject> = {
      '&& .MuiDataGrid-virtualScrollerContent': {
        flexBasis: `${measuredRowHeights.reduce((sum, height) => sum + height, 0)}px !important`,
      },
    };
    measuredRowHeights.forEach((height, index) => {
      const heightPx = `${height}px`;
      sxBySelector[
        `&& .MuiDataGrid-virtualScrollerRenderZone > .MuiDataGrid-row:nth-of-type(${index + 1})`
      ] = {
        '--height': heightPx,
        minHeight: `${heightPx} !important`,
        maxHeight: `${heightPx} !important`,
        height: `${heightPx} !important`,
        '& .MuiDataGrid-cell': {
          '--height': heightPx,
          minHeight: `${heightPx} !important`,
          maxHeight: `${heightPx} !important`,
          height: `${heightPx} !important`,
        },
      };
    });
    return sxBySelector;
  }, [measuredRowHeights]);

  const holdReadyTableForMeasurement =
    previousDataStateRef.current === 'loading' &&
    dataState === 'ready' &&
    hasRows &&
    dataTable !== null &&
    skeleton !== null &&
    !readyTableMeasured;

  useLayoutEffect(() => {
    if (!holdReadyTableForMeasurement) return;
    measureRows();
    setReadyTableMeasured(true);
  }, [holdReadyTableForMeasurement, measureRows]);

  const measuredDataTable =
    dataTable === null ? null : (
      <Box
        ref={rowMeasureRef}
        sx={
          holdReadyTableForMeasurement
            ? {
                pointerEvents: 'none',
                visibility: 'hidden',
                width: '100%',
                minWidth: 0,
              }
            : { width: '100%', minWidth: 0 }
        }
      >
        {dataTable}
      </Box>
    );
  const anchoredSkeleton =
    showSkeletonGrid && skeletonHeightAnchor !== null ? (
      <>
        <Box
          ref={rowMeasureRef}
          aria-hidden="true"
          sx={{
            pointerEvents: 'none',
            visibility: 'hidden',
          }}
        >
          {skeletonHeightAnchor}
        </Box>
        <Box
          sx={
            measuredSkeletonRowSx === null
              ? {
                  backgroundColor: 'background.paper',
                  inset: 0,
                  overflow: 'hidden',
                  position: 'absolute',
                }
              : [
                  {
                    backgroundColor: 'background.paper',
                    inset: 0,
                    overflow: 'hidden',
                    position: 'absolute',
                  },
                  measuredSkeletonRowSx,
                ]
          }
        >
          {skeleton}
        </Box>
      </>
    ) : null;
  const fallbackContent = showSkeletonGrid
    ? skeleton
    : showPlaceholderGrid
      ? placeholder
      : measuredDataTable;
  const measuredReadyTable =
    holdReadyTableForMeasurement && measuredDataTable !== null ? (
      <>
        {measuredDataTable}
        <Box
          sx={{
            backgroundColor: 'background.paper',
            inset: 0,
            overflow: 'hidden',
            position: 'absolute',
          }}
        >
          {skeleton}
        </Box>
      </>
    ) : null;
  const frameContent = anchoredSkeleton ?? measuredReadyTable ?? fallbackContent;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        data-testid="grid-ready"
        data-state={dataState}
        data-error={dataError ?? ''}
        sx={{ display: 'none' }}
      />
      <Box
        data-state={dataState}
        data-fetching={isFetching ? 'true' : 'false'}
        sx={[
          {
            height: resolvedHeight ?? 'auto',
            width: '100%',
            minWidth: 0,
            backgroundColor: 'background.paper',
            borderRadius: r('none'),
            boxShadow: 'none',
            boxSizing: 'border-box',
            position: 'relative',
            overflow: 'hidden',
            overflowAnchor: 'none',
            '& .MuiDataGrid-root': { border: 'none' },
          },
          ...containerStyles,
        ]}
      >
        {frameContent}
      </Box>
      {showLastUpdated ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: s('sm'), pt: s('sm') }}>
          <LastUpdated timestamp={lastUpdatedAt} />
        </Box>
      ) : null}
    </Box>
  );
};
